import React, { useEffect, useMemo } from 'react';
import CompanyHotelDashboardFilter from '@app/model/company_hotel/settings/CompanyHotelDashboardFilter';
import {
	useMarketStateFilterInputTools,
} from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import MenuButtonFilter, { MenuButtonFilterItem } from '@app/component/menu/MenuButtonFilter';
import ParityFilterInput from '@app/model/parity/input/ParityFilterInput';
import MarketStateFilterInput from '@app/model/market_state/input/MarketStateFilterInput';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { FilterConfig } from '@app/site/secured/rateshopper/rates/provider/MarketStateFilterInputProvider';
import DataProvider from '@app/model/_common/DataProvider';

type DashboardSelectNightsFilterProps = {
	filter: MarketStateFilterInput,
	onFilter: (filter: MarketStateFilterInput | ParityFilterInput) => void,
	overrideDefaultConfiguration?: FilterConfig,
	restrictNights?: boolean
}

const DEFAULT_NIGHT_OPTIONS: MenuButtonFilterItem<number>[] = [
	{ name: '1 Night', value: 1 },
	{ name: '2 Nights', value: 2 },
	{ name: '3 Nights', value: 3 },
	{ name: '4 Nights', value: 4 },
	{ name: '5 Nights', value: 5 },
	{ name: '6 Nights', value: 6 },
	{ name: '7 Nights', value: 7 },
	{ name: '8 Nights', value: 8 },
];

const TUI_NIGHT_OPTIONS: MenuButtonFilterItem<number>[] = [
	{ name: '3 Nights', value: 3 },
	{ name: '5 Nights', value: 5 },
	{ name: '7 Nights', value: 7 },
];

const TUI_DEFAULT_NIGHTS = 7;

const DashboardSelectNightsFilter = React.memo(
	function DashboardSelectNightsFilter({ filter, onFilter, restrictNights, overrideDefaultConfiguration }: DashboardSelectNightsFilterProps) {
		const { isDashboardFilterEnabled } = useMarketStateFilterInputTools();
		const providerSupportsFilter = overrideDefaultConfiguration !== undefined ? overrideDefaultConfiguration.enabled : isDashboardFilterEnabled(CompanyHotelDashboardFilter.NIGHTS);

		const { companyHotel } = useSelectedCompanyHotel();
		const isTuiProvider = filter.provider === DataProvider.TUIDE || filter.provider === DataProvider.TUIUK;

		const menuItems = useMemo(() => {
			if (isTuiProvider) return TUI_NIGHT_OPTIONS;

			const parsingNights = companyHotel.parsingSettings?.[0]?.nights;
			if (restrictNights === true && parsingNights !== undefined && parsingNights.length > 0) {
				return parsingNights.map(night => {
					return { name: night + ' Nights', value: night };
				});
			}

			return DEFAULT_NIGHT_OPTIONS;
		}, [companyHotel.parsingSettings, isTuiProvider, restrictNights]);

		useEffect(() => {
			if (isTuiProvider && !TUI_NIGHT_OPTIONS.some(item => item.value === filter.nights)) {
				onFilter({ ...filter, nights: TUI_DEFAULT_NIGHTS });
			}
		}, [filter, isTuiProvider, onFilter]);

		const onSelect = (nights: number) => {
			onFilter({ ...filter, nights: nights });
		};

		return <MenuButtonFilter
			menuItems={menuItems}
			selectedValue={filter.nights}
			onSelect={onSelect}
			menuDescription="Please select the number of nights your wish to compare."
			disabled={!providerSupportsFilter}
		/>;

	},
);

export default DashboardSelectNightsFilter;
